import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  ScaleIcon,
  LockClosedIcon,
  ArrowPathIcon,
  ChartBarIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ============================================================
   Alvarez & Marsal × Clarid — partnership proposal page.
   Standalone, sent directly to A&M. Content is grounded in
   verified public facts about A&M (see research 2026-07-15);
   unverified brief claims (named FCC leaders, "Banking on AI"
   brochure, "Spring Policy Forum", "TDA" team, Workday/Oracle
   alliances, 2011 FSG origin) were intentionally EXCLUDED.
   Clarid is positioned on what it actually does — fair lending,
   HMDA, CRA, marketing, exam packaging + AI governance — NOT
   BSA/AML, which is A&M's expertise and not a Clarid module.
   ============================================================ */

/* ─── Animation helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Denial-rate and pricing-disparity testing, redlining analysis with census-tract mapping and demographic overlays, marketing screening for prohibited-basis language and steering, paired-file review, and formal risk assessments — output tuned to FDIC, OCC, Fed, and NCUA examination standards.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder',
    description:
      '160+ edit checks with plain-language explanations, ML-assisted column matching for LOS imports (Encompass, Jack Henry, LaserPro, Calyx, MeridianLink), geocoding, data-quality scoring, and submission-ready exports.',
  },
  {
    icon: BuildingLibraryIcon,
    title: 'CRA Analysis',
    description:
      'Assessment-area construction, lending-distribution and geographic analysis, small-business and community-development evaluation, peer benchmarking, and examiner-ready performance-evaluation tables for small, intermediate, and large-bank exams.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression & Statistical Testing',
    description:
      'Logistic regression for denial and steering models, OLS regression for pricing disparity, and matched-pair analysis for file review — regulator-aware, auto-triggered on upload, and validated for model soundness under the 2026 interagency model risk guidance (SR 26-2 / OCC Bulletin 2026-13).',
  },
  {
    icon: EyeIcon,
    title: 'Marketing Compliance',
    description:
      'Every ad, email, social post, and disclosure checked against federal rules (Reg Z, UDAAP, TILA, FTC Act, and more) with AI detection, deterministic rules-engine verification, and mandatory human approval — with an examiner-ready trail on every check.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Exam & Board Packaging',
    description:
      'The cross-module differentiator: findings from marketing, HMDA, fair lending, and CRA assemble into a single examiner-ready package and a board-ready summary — one connected record, timestamped and hash-verified, not a folder of disconnected reports.',
  },
];

const governanceControls = [
  {
    icon: CheckCircleIcon,
    title: 'Human-in-the-Loop by Design',
    description:
      "Clarid's three-layer architecture enforces separation: AI performs detection, a deterministic rules engine performs the compliance determination, and a person makes the final decision. Every AI-generated finding requires review and approval through role-based workflows. No autonomous compliance decisions.",
    refs: 'FS AI RMF: MG-1.2, MP-1.2, MP-4.4',
  },
  {
    icon: LockClosedIcon,
    title: 'Hash-Chained Audit Trail',
    description:
      'Every check, finding, approval, and override is recorded in a SHA-256 hash-chained, append-only log where each entry cryptographically links to its predecessor — making tampering mathematically detectable. 60+ event types, timestamped, attributed to a user and a model, and examiner-exportable.',
    refs: 'FS AI RMF: MG-4.4',
  },
  {
    icon: ChartBarIcon,
    title: 'Model Validation & Bias Testing',
    description:
      'Formal model validation against curated test fixtures per AI use case, plus separate bias testing across protected-class dimensions using Fisher\'s exact test for statistical significance — flagging at p < 0.05 with a rate difference above 10%, consistent with NIST AI 100-1 guidance.',
    refs: 'FS AI RMF: MS-2.1–2.5, MS-4.1, MS-4.3',
  },
  {
    icon: ArrowPathIcon,
    title: 'Multi-Provider Resilience',
    description:
      'Clarid runs across three foundation-model providers — OpenAI, Anthropic, and Google — with a circuit-breaker pattern and automatic failover. No single-vendor dependency; model selection is rule-based, deterministic, and auditable.',
    refs: 'FS AI RMF: MS-2.6',
  },
];

const fitModels = [
  {
    num: '01',
    title: 'AI-Governance Execution Layer',
    description:
      "A&M is building the advisory narrative for AI governance in banking — an AI Governance practice, a Global AI Board, an AI Maturity Assessment, and a public goal of drawing roughly half its revenue from AI by 2028. Clarid is a live, examiner-defensible AI compliance platform mapped to the Treasury's FS AI RMF. A&M sets the framework; Clarid is the governed system that demonstrably satisfies it — a reference implementation A&M can put in front of clients today.",
  },
  {
    num: '02',
    title: 'Community-Bank Consumer-Compliance Execution',
    description:
      "A&M advises community and regional banks — with former community-bank regulators on staff — on fair lending, HMDA, CRA, and safety-and-soundness. Clarid is the technology that executes and sustains those programs continuously between engagements. A&M provides the strategic judgment; Clarid provides the ongoing, examiner-ready execution that keeps the standard in place after the team leaves.",
  },
  {
    num: '03',
    title: 'Remediation & Continuous Monitoring',
    description:
      'When A&M is brought in on a fair-lending, CRA, or HMDA finding — often at the highest-urgency moment in the relationship — the engagement typically ends with recommendations the bank must then operationalize. Clarid becomes the monitoring layer that maintains the remediated state, turning a point-in-time fix into continuous, provable compliance.',
  },
  {
    num: '04',
    title: 'The Vertical Complement to a Horizontal Stack',
    description:
      "A&M's public technology alliances — Microsoft, ServiceNow, Salesforce — are horizontal enterprise platforms. None is a vertical, regulator-grade system purpose-built for HMDA, CRA, fair lending, and marketing compliance at community-bank scale. That slot is open, and it is exactly what Clarid was built to fill.",
  },
  {
    num: '05',
    title: 'A BSA/AML Roadmap That Meets Your Financial-Crime Practice',
    description:
      "Consumer compliance is Clarid's core today — but BSA/AML is a deliberate next chapter, being built under a senior financial-crime practitioner with nearly four decades of experience. That roadmap runs straight into A&M's deepest strength. As the capability comes online, A&M's financial-crime practice is positioned to help shape an examiner-grade AML product from the ground up — and the same platform that carries A&M's consumer-compliance advisory would carry its AML engagements too. A second, deeper surface for the partnership.",
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF gap analysis (control objectives mapped across GOVERN, MAP, MEASURE, MANAGE)',
  'Model inventory with validation framework and per-provider model cards',
  'Bias-testing methodology & results',
  'Data-flow architecture documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan & Business Continuity Plan',
  'Vendor inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: "Explore alignment between A&M's financial-services and AI-governance practices and Clarid's compliance platform." },
  { num: 2, title: 'Technical & Governance Review', description: 'Share the FS AI RMF gap analysis, AI governance policy, model inventory, and data-flow architecture for A&M\'s evaluation.' },
  { num: 3, title: 'Joint Client Pilot', description: 'Identify 2–3 community-bank clients for a structured pilot with defined success criteria.' },
  { num: 4, title: 'Partnership Formalization', description: 'Define the model — referral, co-sell, or embedded-in-engagement — and the go-to-market approach.' },
];

/* ─── Component ─── */
export default function AlvarezMarsalPartnerPage() {
  useEffect(() => {
    document.title = 'Alvarez & Marsal + Clarid — AI-Native Compliance for Community Banks';
    const metaTags: Record<string, string> = {
      'og:title': 'Alvarez & Marsal + Clarid — Partnership Proposal',
      'og:description': 'The AI-native, examiner-grade compliance platform for the community banks A&M advises. Built by a community-bank CMO, validated by former FDIC and Federal Reserve examiners.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'robots': 'noindex, nofollow',
    };
    const created: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute(property === 'robots' ? 'name' : 'property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      created.push(meta);
    }
    return () => {
      created.forEach((t) => document.head.removeChild(t));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Header · lockup only ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
            <span className="text-slate-300 text-xl font-light">×</span>
            <span className="font-display text-2xl font-bold text-navy-900 tracking-tight">Alvarez &amp; Marsal</span>
          </div>
        </div>
      </header>

      {/* ─── 1 · Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">Partnership Proposal</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              The execution layer for the compliance advice A&amp;M already gives
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.6rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance operating system for community banks — built by a
              community-bank CMO, validated by a former FDIC Supervisory Examiner and a former Federal
              Reserve fair-lending subject-matter expert. It is the productized, examiner-grade platform
              that turns Alvarez &amp; Marsal's advisory into continuous execution.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 2 · The Moment ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                A&amp;M has the framework. The market needs an execution layer.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  A&amp;M has made one of the most aggressive AI commitments in professional services — an AI
                  Governance practice, a Global AI Board, an AI Maturity Assessment, and a publicly stated goal
                  of drawing roughly half its revenue, as much as $3.5&nbsp;billion, from AI-related work by 2028.
                  Its 2024 U.S. Financial Services Industry practice put risk and compliance at the center of that push.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  A&amp;M also has deep community-bank credibility — former bank regulators on staff, including a former
                  OCC official who supervised community banks — and a financial-services practice that already advises
                  these institutions on fair lending, HMDA, CRA, and safety-and-soundness.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  What A&amp;M publishes a point of view on — AI governance and modern compliance for community banks —
                  still has no productized, examiner-grade delivery layer behind it. Advisory sets the standard;
                  something has to sustain it after the engagement ends, at a scale community banks can actually afford.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  That's the layer Clarid was built to be.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── 3 · Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              What Clarid does for the banks you serve
            </h2>
            <p className="text-lg text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              Consumer-compliance and fair-lending execution — the daily workflows community banks manage and
              examiners scrutinize. Clarid's platform today centers on consumer compliance; BSA/AML is a
              deliberate next expansion (more on that below).
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid also meets bankers inside the tools they already use — Microsoft Copilot, Claude, ChatGPT —
              through the Model Context Protocol (MCP). Compliance intelligence in the daily workflow, not one more portal.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 4 · AI Governance / FS AI RMF ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '48px 48px' }} />
        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">AI Governance, Made Concrete</p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                What A&amp;M advises, Clarid already implements
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                The U.S. Treasury's Financial Services AI Risk Management Framework is the sector's blueprint for
                governed AI. Clarid maps to it not as a retrofit but by construction — examiner defensibility was the
                first design principle. For an advisor building an AI-governance point of view, Clarid is a working,
                inspectable example of governed AI in production.
              </p>
            </div>
          </FadeUp>
          <div className="space-y-6 lg:space-y-8 mt-14">
            {governanceControls.map((c, i) => (
              <ScaleIn key={c.title} delay={i * 0.08}>
                <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-5">
                    <c.icon className="h-7 w-7 text-teal-400 flex-shrink-0" />
                    <h3 className="text-2xl font-display font-bold text-white">{c.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed mb-4">{c.description}</p>
                  <p className="text-sm font-mono text-slate-500">{c.refs}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 5 · Built by the buyer, validated by examiners ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built with the examination perspective
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Validated by former FDIC and Federal Reserve examiners</h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve fair-lending subject-matter expert —
                  with decades of combined experience across hundreds of community-bank examinations — validate Clarid's
                  rules, workflows, and outputs against what examiners actually evaluate.
                </p>
              </div>
            </ScaleIn>
            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Built by the buyer</h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community-bank CMO who manages marketing compliance, HMDA reporting, and
                  examiner relationships as part of his daily work. This wasn't designed by engineers guessing at
                  compliance needs — it was built by someone who sits across from examiners using its outputs.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── 6 · Where Clarid fits A&M ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                Where Clarid fits Alvarez &amp; Marsal
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Four ways the platform slots into work A&amp;M is already doing. Any one is a starting point.
              </p>
            </FadeUp>
            <div className="space-y-6 lg:space-y-8">
              {fitModels.map((m) => (
                <ScaleIn key={m.num} delay={0.05}>
                  <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">{m.num}</span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{m.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>
            <FadeUp delay={0.3}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We'd welcome a conversation about which model — or combination — aligns best with A&amp;M's client
                strategy and partner framework.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── 7 · Due diligence ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">We've done the homework</h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a vendor due-diligence package aligned to interagency guidance on third-party
                relationships. Available for review:
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.12 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.5}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                SOC 2 Type II is in progress with an independent auditor; controls are documented and the program is
                underway. Our executive due-diligence summary and full FS AI RMF gap analysis are available on request.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── 8 · Moving forward ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">Moving forward</h2>
            </FadeUp>
            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">{step.num}</div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 9 · Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder &amp; CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">bobby@clarid.ai</a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-slate-400">
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
            <span className="text-slate-200">×</span>
            <span className="font-display font-semibold text-sm">Alvarez &amp; Marsal</span>
          </div>
          <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} Clarid. Confidential — prepared for Alvarez &amp; Marsal.</p>
          <div className="flex items-center gap-4 text-sm">
            <a href="/security" className="text-slate-400 hover:text-teal-600 transition-colors">Security</a>
            <a href="/privacy" className="text-slate-400 hover:text-teal-600 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>

      <div className="sr-only">
        Prepared by Clarid for Alvarez &amp; Marsal. This page is a partnership proposal from Clarid and does not imply
        an existing relationship with or endorsement by Alvarez &amp; Marsal.
      </div>
    </div>
  );
}
